// src/customHooks/useColumnVisibility.ts (or wherever the hook is defined)

import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { initialState, setHiddenColumns, toggleColumn } from '../../features/columnVisibilitySlice';
import { useAppSelector } from '../../hooks';
import { selectAuth } from '../../features/authSlice';

interface UseColumnVisibilityProps {
  pageName: string;
}

const useColumnVisibility = ({ pageName }: UseColumnVisibilityProps) => {
  const dispatch = useDispatch();
  const pageFromStore = useSelector((state: RootState) => state.columnVisibility.pages[pageName]);
  const defaults = useMemo(() => initialState.pages[pageName] || [], [pageName]);
  const hiddenColumns =
    pageFromStore && pageFromStore.length > 0 ? pageFromStore : defaults;
  const { userAccountID } = useAppSelector(selectAuth);

  // INITIALISATION – runs only once (or when pageName/userAccountID changes)
  useEffect(() => {
    if (!pageName || defaults.length === 0 || !userAccountID) return;

    const storedRaw = localStorage.getItem(`hiddenColumns_${userAccountID}`);
    let parsed: Record<string, string[]> = {};
    try {
      parsed = storedRaw ? JSON.parse(storedRaw) : {};
    } catch {
      parsed = {};
    }

    const stored = parsed[pageName] || [];

    // Soft-add newly introduced default columns (e.g. lastLeaveDt) after lastLoginDt when that column is visible
    let effectiveStored = stored
    if (pageName === 'expenseUsers' && stored.includes('lastLoginDt') && !stored.includes('lastLeaveDt')) {
      effectiveStored = [...stored]
      effectiveStored.splice(stored.indexOf('lastLoginDt') + 1, 0, 'lastLeaveDt')
      parsed[pageName] = effectiveStored
      localStorage.setItem(`hiddenColumns_${userAccountID}`, JSON.stringify(parsed))
    }
    if (pageName === 'expenseHistory' && !stored.includes('createdBy') && stored.length > 0) {
      effectiveStored = [...effectiveStored]
      const insertAt = effectiveStored.includes('updatedDate')
        ? effectiveStored.indexOf('updatedDate') + 1
        : effectiveStored.length
      effectiveStored.splice(insertAt, 0, 'createdBy')
      parsed[pageName] = effectiveStored
      localStorage.setItem(`hiddenColumns_${userAccountID}`, JSON.stringify(parsed))
    }
    if (pageName === 'corpCreditCardsApprovalQueue' && effectiveStored.length > 0 && effectiveStored.length < defaults.length) {
      effectiveStored = defaults
      parsed[pageName] = defaults
      localStorage.setItem(`hiddenColumns_${userAccountID}`, JSON.stringify(parsed))
    }

    if (effectiveStored.length === 0) {
      // No saved data → use defaults
      dispatch(setHiddenColumns({ page: pageName, hiddenColumns: defaults }));
      parsed[pageName] = defaults;
      localStorage.setItem(`hiddenColumns_${userAccountID}`, JSON.stringify(parsed));
    } else {
      // Saved data exists → use it (only sync Redux if it's empty)
      if (!pageFromStore || pageFromStore.length === 0) {
        dispatch(setHiddenColumns({ page: pageName, hiddenColumns: effectiveStored }));
      } else if (
        pageName === 'expenseUsers' &&
        pageFromStore.includes('lastLoginDt') &&
        !pageFromStore.includes('lastLeaveDt')
      ) {
        const next = [...pageFromStore]
        next.splice(pageFromStore.indexOf('lastLoginDt') + 1, 0, 'lastLeaveDt')
        dispatch(setHiddenColumns({ page: pageName, hiddenColumns: next }))
      } else if (pageName === 'expenseHistory' && !pageFromStore.includes('createdBy')) {
        const next = [...pageFromStore]
        const insertAt = next.includes('updatedDate') ? next.indexOf('updatedDate') + 1 : next.length
        next.splice(insertAt, 0, 'createdBy')
        dispatch(setHiddenColumns({ page: pageName, hiddenColumns: next }))
      } else if (
        pageName === 'corpCreditCardsApprovalQueue' &&
        pageFromStore &&
        pageFromStore.length < defaults.length
      ) {
        dispatch(setHiddenColumns({ page: pageName, hiddenColumns: defaults }))
      }
    }
    // This effect should NOT depend on `pageFromStore` – we only want to run once.
  }, [pageName, defaults, userAccountID]); // <- removed pageFromStore

  // SAVE to localStorage whenever the store changes (after toggles)
  useEffect(() => {
    if (!userAccountID || !pageName) return;
    // Avoid overwriting with an empty array if defaults exist
    if (!hiddenColumns.length && defaults.length > 0) return;

    const storedRaw = localStorage.getItem(`hiddenColumns_${userAccountID}`);
    const parsed = storedRaw ? JSON.parse(storedRaw) : {};
    parsed[pageName] = hiddenColumns;
    localStorage.setItem(`hiddenColumns_${userAccountID}`, JSON.stringify(parsed));
  }, [hiddenColumns, pageName, userAccountID, defaults.length]);

  const handleToggleColumn = (columnId: string) => {
    dispatch(toggleColumn({ page: pageName, columnId }));
  };

  return {
    hiddenColumns,
    handleToggleColumn,
  };
};

export default useColumnVisibility;